<template>
    <div class="actions-container" :class="{ 'two-columns': twoColumns, 'one-column': oneColumn }">
        <div v-for="action in visibleActions" :key="action.event" class="action-wrapper">
            <v-tooltip v-if="!action.showText" top>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        fab
                        x-small
                        depressed
                        :color="toneColor(action.tone)"
                        :outlined="action.tone !== 'primary'"
                        class="action-button"
                        v-bind="attrs" 
                        v-on="on"
                        @click="$emit(action.event, user)">
                        <v-icon small>{{ action.icon }}</v-icon>
                    </v-btn>
                </template>
                <span>{{ action.label }}</span>
            </v-tooltip>
            
            <v-btn
                v-else
                small
                depressed
                :color="toneColor(action.tone)"
                :outlined="action.tone !== 'primary'"
                class="action-button text-button"
                @click="$emit(action.event, user)">
                <v-icon small class="mr-2">{{ action.icon }}</v-icon>
                {{ action.label }}
            </v-btn>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        user: Object,
        showActions: {
            type: Array,
            required: true
        },
        showTextFor: {
            type: Array,
            default: () => []
        },
        twoColumns: {
            type: Boolean,
            default: false
        },
        // Apila las acciones a lo ancho, una por fila. Se usa para listas
        // cortas de acciones sensibles, donde una grilla par dejaría un hueco.
        oneColumn: {
            type: Boolean,
            default: false
        }
    },
    methods: {
        // Un color = un significado. Antes cada acción traía el suyo (14 en
        // total: blue, green, purple, teal, indigo, #FF8A65…), así que el color
        // no distinguía nada: competía con el verde de "Activo" y con la escala
        // de estado del chip de mascota en la misma pantalla.
        toneColor(tone) {
            return {
                primary: 'primary',   // la acción esperada
                danger:  'error',     // destructiva o de riesgo alto
                warning: 'warning'    // advertencia reversible
            }[tone] || 'secondary';   // el resto: neutras, sin jerarquía entre sí
        }
    },
    computed: {
        allActions() {
            return {
                edit: {
                    icon: 'mdi-pencil',
                    tone: 'primary',
                    label: 'Editar',
                    event: 'edit'
                },
                addPet: {
                    icon: 'mdi-wallet-plus',
                    tone: 'neutral',
                    label: 'Agregar Membresía',
                    event: 'addPet'
                },
                pdf: {
                    icon: 'mdi-file-document',
                    tone: 'neutral',
                    label: 'Ver membresías',
                    event: 'pdf'
                },
                delete: {
                    icon: 'mdi-delete',
                    tone: 'danger',
                    label: 'Eliminar usuario',
                    event: 'delete'
                },
                back: {
                    icon: 'mdi-arrow-left',
                    tone: 'neutral',
                    label: 'Volver',
                    event: 'back'
                },
                ressetPassword: {
                    icon: 'mdi-lock-reset',
                    tone: 'neutral',
                    label: 'Reestablecer Contraseña',
                    event: 'ressetPassword'
                },
                validateEmail: {
                    icon: 'mdi-email-check',
                    tone: 'neutral',
                    label: 'Validar Correo',
                    event: 'validateEmail'
                },
                historyPayment: {
                    icon: 'mdi-history',
                    tone: 'neutral',
                    label: 'Historial de Pagos',
                    event: 'historyPayment'
                },
                updateContract: {
                    icon: 'mdi-file-document-edit',
                    tone: 'neutral',
                    label: 'Actualizar/Crear contrato',
                    event: 'updateContract'
                },
                statusMembership: {
                    icon: 'mdi-history',
                    tone: 'neutral',
                    label: 'Cambiar estado',
                    event: 'statusMembership'
                },
                seeContract: {
                    icon: 'mdi-file-document-outline',
                    tone: 'neutral',
                    label: 'Ver contrato',
                    event: 'seeContract'
                },
                updateMembership: {
                    icon: 'mdi-cog-outline',
                    tone: 'neutral',
                    label: 'Gestionar membresía',
                    event: 'updateMembership'
                },
                renewMembership: {
                    icon: 'mdi-autorenew',
                    tone: 'neutral',
                    label: 'Renovar membresía',
                    event: 'renewMembership'
                },
                regeneratePaymentLink: {
                    icon: 'mdi-link-variant',
                    tone: 'neutral',
                    label: 'Regenerar link de pago',
                    event: 'regeneratePaymentLink'
                },
                manageSponsorship: {
                    icon: 'mdi-cog-outline',
                    tone: 'neutral',
                    label: 'Gestionar apadrinamiento',
                    event: 'manageSponsorship'
                },
                changeSponsoredPet: {
                    icon: 'mdi-swap-horizontal',
                    tone: 'neutral',
                    label: 'Cambiar mascota apadrinada',
                    event: 'changeSponsoredPet'
                },
                addCoTutor: {
                    icon: 'mdi-account-plus',
                    tone: 'neutral',
                    label: 'Agregar Co-tutor',
                    event: 'addCoTutor'
                },
                addNote: {
                    icon: 'mdi-note-plus',
                    tone: 'neutral',
                    label: 'Agregar Nota',
                    event: 'addNote'
                },
                // [NUEVO] Lista Negra
                blacklist: {
                    icon: 'mdi-cancel',
                    tone: 'danger',
                    label: 'Lista Negra',
                    event: 'blacklist'
                },
                // [NUEVO] Marcar como sospechoso (019-mark-suspicious-entities-ui)
                suspicious: {
                    icon: 'mdi-alert-circle-outline',
                    tone: 'warning',
                    label: 'Marcar como sospechoso',
                    event: 'suspicious'
                },
                // Operativa, no un juicio sobre la persona: invalida una
                // sesión vigente igual que reestablecer la contraseña
                // invalida la anterior. Teñirla competiría con "sospechoso",
                // que sí es una advertencia sobre el usuario.
                forceLogout: {
                    icon: 'mdi-logout',
                    tone: 'neutral',
                    label: 'Forzar Cierre de Sesión',
                    event: 'forceLogout'
                }
            };
        },
        visibleActions() {
            return this.showActions
                .map(actionName => ({
                    ...this.allActions[actionName],
                    showText: this.showTextFor.includes(actionName)
                }))
                .filter(action => action);
        }
    }
};
</script>

<style scoped>
.actions-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}
.actions-container.two-columns {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    width: 100%;
}
.action-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
}
.text-button {
    width: 70%;
    min-width: auto;
    justify-content: center;
    font-size: 0.56rem !important;
}
.text-button .v-btn__content {
    width: 100%;
    justify-content: center;
}
.text-button .v-icon {
    font-size: 15px !important;
}
.actions-container.one-column {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    width: 100%;
}
.two-columns .action-wrapper,
.one-column .action-wrapper {
    width: 100%;
}
/* Botón a lo ancho de su celda: ícono y texto alineados a la izquierda, en
   caja alta natural y con más cuerpo que la fila compacta por defecto. */
.two-columns .text-button,
.one-column .text-button {
    min-width: unset;
    width: 100%;
    height: 34px !important;
    padding: 0 12px !important;
    border-radius: 6px;
    font-size: 0.72rem !important;
    font-weight: 600;
    letter-spacing: normal;
    text-transform: none;
}
.two-columns .text-button >>> .v-btn__content,
.one-column .text-button >>> .v-btn__content {
    width: 100%;
    justify-content: flex-start;
}
.two-columns .text-button .v-icon,
.one-column .text-button .v-icon {
    font-size: 17px !important;
}
</style>